/** The `trees` kind's options (backend `viewer/report_kinds.py`, TREES_OPTIONS) as the page reads them. */
export type AnalysisTab = 'height' | 'strata' | 'dbh' | 'volume';
export const ANALYSIS_TABS: AnalysisTab[] = ['height', 'strata', 'dbh', 'volume'];

export interface Threshold {
  genus: string;
  min: number;
  max: number;
}

export interface TreesConfig {
  sections: {
    summary: { enabled: boolean; prose: boolean };
    map: { enabled: boolean };
    species: { enabled: boolean };
    analysis: { enabled: boolean; tabs: Record<AnalysisTab, boolean> };
    maturity: { enabled: boolean; thresholds: Threshold[] };
  };
}

export const TREES_DEFAULTS: TreesConfig = {
  sections: {
    summary: { enabled: true, prose: true },
    map: { enabled: true },
    species: { enabled: true },
    analysis: { enabled: true, tabs: { height: true, strata: true, dbh: true, volume: true } },
    maturity: { enabled: true, thresholds: [] },
  },
};

const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

const isThreshold = (r: unknown): r is Threshold =>
  isObject(r) && typeof r.genus === 'string' && typeof r.min === 'number' && typeof r.max === 'number';

function merge<T>(base: T, over: unknown): T {
  if (Array.isArray(base)) return (Array.isArray(over) ? over.filter(isThreshold) : base) as T;
  if (isObject(base)) {
    if (!isObject(over)) return base;
    const out: Record<string, unknown> = { ...base };
    for (const key of Object.keys(base)) out[key] = merge(base[key], over[key]);
    return out as T;
  }
  return (typeof over === typeof base ? over : base) as T;
}

/** The stored options over the defaults, key by key: a report saved before a key existed still renders. */
export function treesConfig(raw: unknown): TreesConfig {
  return merge(TREES_DEFAULTS, raw);
}

export function enabledTabs(config: TreesConfig): AnalysisTab[] {
  const { analysis } = config.sections;
  return analysis.enabled ? ANALYSIS_TABS.filter((tab) => analysis.tabs[tab]) : [];
}
